/**
 * A statement about the whole view.
 *
 * Errors arrived in four presentations — `.error`, `.banner-warn`, a fault
 * toast, `.field-error` — chosen per author rather than per severity. This is
 * the one for anything that qualifies everything below it: a failed load, a
 * filtered list, a number that covers less than was asked for.
 *
 * Per-field errors stay on the field (`Field`'s `error`), and a confirmation
 * of something the screen already shows stays a `Toast`.
 */

import { CircleAlert, CircleCheck, Info, TriangleAlert } from 'lucide-react';
import type React from 'react';

export type BannerTone = 'info' | 'warn' | 'fault' | 'ok';

const ICON: Record<BannerTone, React.ReactNode> = {
  info: <Info size={16} />,
  warn: <TriangleAlert size={16} />,
  fault: <CircleAlert size={16} />,
  ok: <CircleCheck size={16} />,
};

export function Banner({
  tone = 'info',
  title,
  children,
  actions,
}: {
  tone?: BannerTone;
  title?: React.ReactNode;
  children?: React.ReactNode;
  actions?: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className={`banner banner-${tone}`} role={tone === 'fault' ? 'alert' : 'status'}>
      <span className="banner-icon">{ICON[tone]}</span>
      <div className="banner-body">
        {title !== undefined && <strong>{title}</strong>}
        {children !== undefined && <div>{children}</div>}
      </div>
      {actions !== undefined && <div className="banner-actions">{actions}</div>}
    </div>
  );
}
